/**
 * Guided Enrollment Modal – streamlined multi-step flow for guided clinics.
 * Redesigned with a cleaner, modern UI: stepper breadcrumbs, card-based
 * program/option selection, and a compact confirmation step.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Send,
  ChevronRight,
  ArrowLeft,
  Loader2,
  X,
  Sparkles,
  Mail,
  RefreshCw,
  Check,
  User,
  Search,
  UserPlus,
} from 'lucide-react';
import { useClinic } from '@/contexts/ClinicContext';
import { getPrograms, type Program, type ProgramOption } from '@/services/storefrontService';
import { createGuidedIntake } from '@/services/guidedIntakeService';
import { patientService } from '@/services/patientService';
import { ReferringProviderSelect } from '@/components/commissions/ReferringProviderSelect';
import type { ApiError } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import GuidedNewPatientForm from '@/components/clinic/GuidedNewPatientForm';

function formatGuidedIntakeError(error: unknown): string {
  const apiError = error as ApiError | undefined;
  if (apiError?.message) {
    const details = apiError.details;
    if (Array.isArray(details) && details.length > 0) {
      const parts = (details as Array<{ field?: string; message?: string }>)
        .map(d => (d.field ? `${d.field}: ${d.message ?? 'invalid'}` : d.message ?? ''))
        .filter(Boolean);
      return parts.length > 0 ? `${apiError.message} ${parts.join(' • ')}` : apiError.message;
    }
    return apiError.message;
  }
  return error instanceof Error ? error.message : 'Failed to send link. Please try again.';
}

const ALL_STEPS = ['patient', 'option', 'confirm'] as const;
type Step = (typeof ALL_STEPS)[number];

const STEP_LABELS: Record<Step, string> = {
  patient: 'Patient',
  option: 'Plan',
  confirm: 'Confirm',
};

interface ProgramOptionWithProgram {
  option: ProgramOption;
  program: Program;
}

type PatientMode = 'search' | 'new';

interface PatientSearchResult {
  id: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  email?: string;
  phone?: string;
}

const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const frequencyLabel: Record<string, string> = {
  weekly: '/week',
  monthly: '/mo',
  quarterly: '/qtr',
};

interface GuidedIntakeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicId: string;
  patientDbId?: string;
  patientEmail?: string;
  patientName?: string;
  onSuccess?: () => void;
}

export const GuidedIntakeModal: React.FC<GuidedIntakeModalProps> = ({
  open,
  onOpenChange,
  clinicId,
  patientDbId: propPatientDbId,
  patientEmail: initialPatientEmail,
  patientName: initialPatientName,
  onSuccess,
}) => {
  const { clinic: _clinic } = useClinic();
  void _clinic;
  const { toast } = useToast();
  const requiresPatientPick = !propPatientDbId;
  const STEPS = useMemo(
    () => (requiresPatientPick ? ALL_STEPS : ALL_STEPS.filter(s => s !== 'patient')),
    [requiresPatientPick]
  ) as readonly Step[];
  const [patientMode, setPatientMode] = useState<PatientMode>('search');
  const [selectedPatient, setSelectedPatient] = useState<PatientSearchResult | null>(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientResults, setPatientResults] = useState<PatientSearchResult[]>([]);
  const [searchingPatients, setSearchingPatients] = useState(false);
  const effectivePatientDbId = propPatientDbId || selectedPatient?.id || '';
  const hasPatientForSubmit = !!effectivePatientDbId;

  const patientName =
    initialPatientName ||
    selectedPatient?.full_name ||
    [selectedPatient?.first_name, selectedPatient?.last_name].filter(Boolean).join(' ') ||
    undefined;

  const [step, setStep] = useState<Step>(requiresPatientPick ? 'patient' : 'option');
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [selectedOption, setSelectedOption] = useState<ProgramOption | null>(null);

  // Flatten all program_options across programs into a single list so the
  // practitioner picks a plan directly, without an intermediate "program" step.
  const allOptions = useMemo<ProgramOptionWithProgram[]>(() => {
    return programs.flatMap(program =>
      (program.program_options || []).map(option => ({ option, program })),
    );
  }, [programs]);

  const selectedProgram = useMemo<Program | null>(() => {
    if (!selectedOption) return null;
    return allOptions.find(x => x.option.id === selectedOption.id)?.program ?? null;
  }, [selectedOption, allOptions]);
  const [patientEmail, setPatientEmail] = useState(initialPatientEmail ?? '');
  const [sendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [referringUserId, setReferringUserId] = useState<string | null>(null);
  const [commissionRateBps, setCommissionRateBps] = useState<number | null>(null);

  useEffect(() => {
    if (initialPatientEmail) setPatientEmail(initialPatientEmail);
  }, [initialPatientEmail]);

  const loadPrograms = useCallback(async () => {
    if (!clinicId) return;
    setLoadingPrograms(true);
    try {
      const result = await getPrograms({ clinicId, limit: 100 });
      const list = result?.programs ?? [];
      const guidedPrograms = list.filter(
        (p) => (p as Program & { display_mode?: string }).display_mode === 'protocol_showcase'
      );
      setPrograms(guidedPrograms);
    } catch (e) {
      console.error('Failed to load programs:', e);
      toast({ title: 'Failed to load programs', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setLoadingPrograms(false);
    }
  }, [clinicId, toast]);

  useEffect(() => {
    if (open && clinicId) {
      loadPrograms();
      setStep(requiresPatientPick ? 'patient' : 'option');
      setSelectedOption(null);
      setSubmitError(null);
      setPatientEmail(initialPatientEmail ?? '');
      setSelectedPatient(null);
      setPatientSearch('');
      setPatientResults([]);
      setPatientMode('search');
    }
  }, [open, clinicId, initialPatientEmail, requiresPatientPick, loadPrograms]);

  useEffect(() => {
    if (!requiresPatientPick || !open || !clinicId) return;
    if (patientMode !== 'search') return;
    let cancelled = false;
    const handle = setTimeout(async () => {
      setSearchingPatients(true);
      try {
        const response = await patientService.getPatientsForDropdown(patientSearch, clinicId);
        if (!cancelled) setPatientResults(response.data.patients || []);
      } catch (e) {
        console.error('Failed to search patients:', e);
        if (!cancelled) setPatientResults([]);
      } finally {
        if (!cancelled) setSearchingPatients(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [patientSearch, requiresPatientPick, open, clinicId, patientMode]);

  const stepIndex = STEPS.indexOf(step);

  const goBack = () => {
    setSubmitError(null);
    if (step === 'option' && requiresPatientPick) setStep('patient');
    else if (step === 'confirm') setStep('option');
  };

  const handleSelectPatient = (p: PatientSearchResult) => {
    setSelectedPatient(p);
    if (p.email) setPatientEmail(p.email);
    setStep('option');
  };

  const handlePatientCreated = (
    created: { id: string; first_name?: string; last_name?: string; full_name?: string; email?: string; phone?: string }
  ) => {
    if (!created?.id) return;
    setSelectedPatient(created);
    if (created.email) setPatientEmail(created.email);
    setStep('option');
  };

  const handleSelectOption = (option: ProgramOption) => {
    setSelectedOption(option);
    setStep('confirm');
  };

  const handleSendIntake = async () => {
    if (!selectedOption || !clinicId) return;
    if (!hasPatientForSubmit) {
      setSubmitError('Select an existing patient or fill in new patient details.');
      return;
    }
    if (referringUserId && commissionRateBps == null) {
      setSubmitError('Commission rate is required when a referring staff is selected.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: Parameters<typeof createGuidedIntake>[1] = {
        patient_id: effectivePatientDbId,
        clinic_program_option_id: selectedOption.id,
        patient_email: patientEmail || undefined,
        send_email: sendEmail,
        ...(referringUserId && { referring_user_id: referringUserId }),
        ...(commissionRateBps != null && { commission_rate_bps: commissionRateBps }),
      };

      const response = await createGuidedIntake(clinicId, payload);
      if (response.success) {
        const message = response.data?.message;
        toast({
          title: 'Link sent',
          description: message ?? 'The patient will receive the intake link.',
          variant: 'success',
        });
        onSuccess?.();
        setTimeout(() => onOpenChange(false), 300);
      } else {
        const msg = response.error?.message ?? 'Failed to send link. Please try again.';
        setSubmitError(msg);
        toast({ title: 'Error', description: msg, variant: 'destructive' });
      }
    } catch (e: unknown) {
      const message = formatGuidedIntakeError(e);
      setSubmitError(message);
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen && !submitting) {
      onOpenChange(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        hideCloseButton
        className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0 gap-0"
        style={{ borderRadius: '1rem' }}
      >
        {/* ── Header ── */}
        <div className="px-5 pt-5 pb-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              {stepIndex > 0 ? (
                <button
                  onClick={goBack}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                  <Send className="h-4 w-4 text-primary" />
                </div>
              )}
              <div>
                <DialogTitle className="text-base font-semibold leading-tight">
                  {step === 'patient' && 'Select a patient'}
                  {step === 'option' && 'Choose a plan'}
                  {step === 'confirm' && 'Confirm & send'}
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                  {patientName ? `Sending link to ${patientName}` : 'Send link to patient'}
                </DialogDescription>
              </div>
            </div>
            <button
              onClick={() => handleClose(false)}
              disabled={submitting}
              className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-muted transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* ── Stepper ── */}
          <div className="flex items-center gap-1">
            {STEPS.map((s, i) => {
              const isActive = i === stepIndex;
              const isComplete = i < stepIndex;
              return (
                <React.Fragment key={s}>
                  <div className="flex items-center gap-1.5">
                    <div
                      className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                        isComplete
                          ? 'bg-primary text-primary-foreground'
                          : isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {isComplete ? <Check className="h-3.5 w-3.5" /> : i + 1}
                    </div>
                    <span
                      className={`text-xs font-medium ${
                        isActive || isComplete ? 'text-foreground' : 'text-muted-foreground'
                      }`}
                    >
                      {STEP_LABELS[s]}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-px mx-1 ${
                        isComplete ? 'bg-primary' : 'bg-border'
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="h-px bg-border" />

        {/* ── Content ── */}
        <div className="overflow-y-auto flex-1 px-5 py-4">
          {/* Step 0: Patient picker (only when no patient was pre-selected) */}
          {step === 'patient' && (
            <div className="space-y-4">
              {/* Mode tabs */}
              <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-muted">
                <button
                  type="button"
                  onClick={() => setPatientMode('search')}
                  className={`flex items-center justify-center gap-1.5 h-8 rounded-md text-xs font-medium transition-colors ${
                    patientMode === 'search'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Search className="h-3.5 w-3.5" />
                  Search existing
                </button>
                <button
                  type="button"
                  onClick={() => setPatientMode('new')}
                  className={`flex items-center justify-center gap-1.5 h-8 rounded-md text-xs font-medium transition-colors ${
                    patientMode === 'new'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  New patient
                </button>
              </div>

              {patientMode === 'search' && (
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or email..."
                      value={patientSearch}
                      onChange={(e) => setPatientSearch(e.target.value)}
                      className="pl-9"
                      autoFocus
                    />
                  </div>
                  <div className="space-y-1.5 max-h-[320px] overflow-y-auto">
                    {searchingPatients && (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    )}
                    {!searchingPatients && patientResults.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-8 gap-2">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                          <User className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <p className="text-sm text-muted-foreground">No patients found.</p>
                      </div>
                    )}
                    {!searchingPatients && patientResults.map((p) => {
                      const label = p.full_name
                        || [p.first_name, p.last_name].filter(Boolean).join(' ')
                        || p.email
                        || 'Unknown';
                      const isSelected = selectedPatient?.id === p.id;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => handleSelectPatient(p)}
                          className={`flex items-center w-full gap-3 p-3 rounded-xl border text-left transition-all ${
                            isSelected
                              ? 'border-primary/40 bg-primary/[0.04]'
                              : 'border-border bg-card hover:border-primary/30 hover:bg-primary/[0.02]'
                          }`}
                        >
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted flex-shrink-0">
                            <User className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{label}</p>
                            {(p.email || p.phone) && (
                              <p className="text-xs text-muted-foreground truncate">
                                {[p.email, p.phone].filter(Boolean).join(' • ')}
                              </p>
                            )}
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {patientMode === 'new' && (
                <GuidedNewPatientForm
                  clinicId={clinicId}
                  onCreated={handlePatientCreated}
                  onCancel={() => setPatientMode('search')}
                />
              )}
            </div>
          )}

          {/* Step 1: Option (flattened across all programs) */}
          {step === 'option' && (
            <div className="space-y-2">
              {loadingPrograms ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3 p-3.5 rounded-xl border border-border">
                      <Skeleton className="h-9 w-9 rounded-lg flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-3/5 rounded" />
                        <Skeleton className="h-3 w-4/5 rounded" />
                      </div>
                      <Skeleton className="h-4 w-4 rounded flex-shrink-0" />
                    </div>
                  ))}
                </div>
              ) : allOptions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                    <Sparkles className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground text-center">
                    No plans available.<br />
                    Add programs with protocol options in Settings.
                  </p>
                </div>
              ) : (
                allOptions.map(({ option: opt, program }) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => handleSelectOption(opt)}
                    className="group flex items-start w-full gap-3 p-4 rounded-xl border border-border bg-card hover:border-primary/30 hover:bg-primary/[0.02] text-left transition-all"
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0 mt-0.5 group-hover:bg-primary/15 transition-colors">
                      <Sparkles className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                          {program.title}
                        </p>
                        {opt.is_subscription && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-blue-200 text-blue-600 bg-blue-50">
                            <RefreshCw className="w-2.5 h-2.5 mr-0.5" />
                            Recurring
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm font-semibold leading-tight">{opt.label}</p>
                      {opt.description && (
                        <p className="text-xs text-muted-foreground leading-relaxed">{opt.description}</p>
                      )}
                      {opt.includes_services && opt.includes_services.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {opt.includes_services.map((svc, idx) => (
                            <span key={idx} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground bg-muted rounded-md px-2 py-0.5">
                              <Check className="w-3 h-3 text-green-500" />
                              {svc}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0 pt-0.5">
                      <p className="text-base font-bold">{formatCurrency(opt.program_price)}</p>
                      {opt.is_subscription && opt.subscription_frequency && (
                        <p className="text-[11px] text-muted-foreground">
                          {frequencyLabel[opt.subscription_frequency] || ''}
                        </p>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 'confirm' && selectedOption && selectedProgram && (
            <div className="space-y-4">
              {/* Summary card */}
              <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-0.5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Program</p>
                    <p className="text-sm font-medium">{selectedProgram.title}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep('option')}
                    className="text-xs text-primary hover:underline"
                  >
                    Change
                  </button>
                </div>
                <div className="h-px bg-border" />
                <div className="flex items-start justify-between">
                  <div className="space-y-0.5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Plan</p>
                    <p className="text-sm font-medium">{selectedOption.label}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold">{formatCurrency(selectedOption.program_price)}</span>
                    {selectedOption.is_subscription && selectedOption.subscription_frequency && (
                      <span className="text-xs text-muted-foreground">
                        {frequencyLabel[selectedOption.subscription_frequency]}
                      </span>
                    )}
                  </div>
                </div>
                <div className="h-px bg-border" />
                <div className="flex items-start justify-between">
                  <div className="space-y-0.5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Shipping</p>
                    <p className="text-sm font-medium">Selected by pharmacy at fulfillment</p>
                  </div>
                </div>
              </div>

              {/* Referring provider + commission (optional) */}
              {clinicId && (
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                  <ReferringProviderSelect
                    clinicId={clinicId}
                    value={{ referringUserId, commissionRateBps }}
                    onChange={(val) => {
                      setReferringUserId(val.referringUserId);
                      setCommissionRateBps(val.commissionRateBps);
                    }}
                    disabled={submitting}
                  />
                </div>
              )}

              {/* Email override */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">
                  <Mail className="h-3 w-3 inline mr-1" />
                  Patient email
                </Label>
                <Input
                  type="email"
                  placeholder={initialPatientEmail || selectedPatient?.email || 'patient@example.com'}
                  value={patientEmail}
                  onChange={(e) => setPatientEmail(e.target.value)}
                  className="h-9 text-sm"
                />
                <p className="text-[11px] text-muted-foreground">
                  Leave blank to use their default email on file.
                </p>
              </div>

              {submitError && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
                  {submitError}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        {step === 'confirm' && (
          <>
            <div className="h-px bg-border" />
            <div className="px-5 py-3.5 flex-shrink-0">
              <Button
                onClick={handleSendIntake}
                disabled={submitting || !hasPatientForSubmit || (!!referringUserId && commissionRateBps == null)}
                className="w-full h-10 gap-2"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {submitting ? 'Sending…' : 'Send link'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GuidedIntakeModal;
